import { useId, type ReactNode } from "react";

import type { LineEligibility, PublicOrderLine } from "@sirdab/returns-shared";
import { Badge, Checkbox, cn } from "@sirdab/ui";

import { formatCount } from "../format/money";
import { useMessages } from "../i18n";
import { Money } from "./Money";

export interface LineItemRowProps {
  line: PublicOrderLine;
  /** Absent on the tracking page, where eligibility is no longer a question. */
  eligibility?: LineEligibility | undefined;
  selected?: boolean;
  onToggle?: ((lineRef: string) => void) | undefined;
  /** Quantity stepper, radio group, evidence thumbnails — supplied by the step. */
  children?: ReactNode;
  /** Trailing slot: a state chip on tracking, a fee on review. */
  aside?: ReactNode;
}

/**
 * One line of the order. ADR-007: an ineligible line stays VISIBLE, greyed, carrying the
 * server's `reasonText`. Hiding it is what turns a self-service return into a WhatsApp
 * message — the row is deliberately not filterable from here.
 *
 * `reasonText` is rendered, never composed. The portal has no opinion about why a line is
 * ineligible; it prints what the policy engine said.
 */
export function LineItemRow({
  line,
  eligibility,
  selected = false,
  onToggle,
  children,
  aside,
}: LineItemRowProps): React.JSX.Element {
  const { text, locale } = useMessages();
  const id = useId();
  const ineligible = eligibility !== undefined && !eligibility.eligible;
  const selectable = onToggle !== undefined && !ineligible;

  return (
    <li
      aria-disabled={ineligible || undefined}
      className={cn(
        "border-border flex gap-3 border-b py-3 last:border-b-0",
        ineligible && "opacity-50",
      )}
    >
      {onToggle !== undefined ? (
        <Checkbox
          id={id}
          checked={selected}
          disabled={!selectable}
          onCheckedChange={() => onToggle(line.lineRef)}
          aria-label={text(line.productName)}
          className="mt-1"
        />
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p className="text-body text-foreground">{text(line.productName)}</p>
        <p className="text-caption-quiet text-subtle flex flex-wrap items-baseline gap-x-2">
          {line.variant !== null ? <span>{line.variant}</span> : null}
          <span dir="ltr" className="tabular-nums">
            &times;{formatCount(line.qty, locale)}
          </span>
          <Money halalas={line.unitPrice} />
        </p>

        {ineligible ? (
          // Server-authored, bilingual, rendered as-is. Never translated, never rewritten.
          <Badge variant="secondary" className="whitespace-normal">
            {text(eligibility.reasonText)}
          </Badge>
        ) : null}

        {children !== undefined ? <div className="mt-1">{children}</div> : null}
      </div>

      {aside !== undefined ? (
        <div className="shrink-0 text-end">{aside}</div>
      ) : null}
    </li>
  );
}
